import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Camera, Sun, Moon, Monitor, FileText, MapPin, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useUser } from '@hooks/useUser';
import { useTheme } from '@hooks/useTheme';
import { useAuth } from '@hooks/useAuth';
import Card from '@components/common/Card';
import Input from '@components/common/Input';
import Button from '@components/common/Button';
import Avatar from '@components/common/Avatar';
import { clsx } from 'clsx';

const profileSchema = z.object({
  name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
});

const billingSchema = z.object({
  cpf: z.string()
    .min(1, 'CPF é obrigatório')
    .refine((v) => /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(v), 'CPF inválido'),
  cep: z.string()
    .min(1, 'CEP é obrigatório')
    .refine((v) => /^\d{5}-\d{3}$/.test(v), 'CEP inválido'),
  logradouro: z.string().min(3, 'Logradouro é obrigatório'),
  numero: z.string().min(1, 'Número é obrigatório'),
  complemento: z.string().optional(),
  bairro: z.string().min(2, 'Bairro é obrigatório'),
  cidade: z.string().min(2, 'Cidade é obrigatória'),
  estado: z.string().length(2, 'Selecione o estado'),
});

const ESTADOS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA',
  'MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN',
  'RS','RO','RR','SC','SP','SE','TO',
];

// Formata CPF: 000.000.000-00
const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

// Formata CEP: 00000-000
const formatCEP = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/(\d{5})(\d)/, '$1-$2');
};

/**
 * Redimensiona e comprime uma imagem para no máximo maxSize x maxSize pixels,
 * retornando uma data URL JPEG com a qualidade especificada.
 */
const resizeImage = (file, maxSize = 200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Manter proporcao, ajustar ao maxSize
        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Erro ao carregar imagem'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
    reader.readAsDataURL(file);
  });
};

const isBillingComplete = (billing) => {
  if (!billing) return false;
  return !!(billing.cpf && billing.cep && billing.logradouro && billing.numero && billing.bairro && billing.cidade && billing.estado);
};

const Profile = () => {
  const { profile, updateProfile, uploadAvatar, isUpdating, isUploadingAvatar } = useUser();
  const { theme, setTheme } = useTheme();
  const { tenant } = useAuth();
  const [isSavingBilling, setIsSavingBilling] = useState(false);
  const fileInputRef = useRef(null);

  const handleAvatarClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleAvatarChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limpar o input para permitir reselecionar o mesmo arquivo
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      return;
    }

    try {
      const dataUrl = await resizeImage(file, 200, 0.8);
      await uploadAvatar(dataUrl);
    } catch {
      // Erro já tratado pelo hook (toast)
    }
  }, [uploadAvatar]);

  const isPaidPlan = tenant?.plan && tenant.plan !== 'free';
  const billingComplete = isBillingComplete(profile?.billingData);

  // --- Formulário de perfil ---
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (profile?.name) {
      reset({ name: profile.name });
    }
  }, [profile, reset]);

  const handleSave = async (data) => {
    await updateProfile(data);
    reset({ name: data.name });
  };

  // --- Formulário de faturamento ---
  const {
    register: registerBilling,
    handleSubmit: handleSubmitBilling,
    reset: resetBilling,
    setValue: setBillingValue,
    formState: { errors: billingErrors, isDirty: isBillingDirty },
  } = useForm({
    resolver: zodResolver(billingSchema),
    defaultValues: {
      cpf: '', cep: '', logradouro: '', numero: '',
      complemento: '', bairro: '', cidade: '', estado: '',
    },
  });

  useEffect(() => {
    if (profile?.billingData) {
      resetBilling({
        cpf: profile.billingData.cpf || '',
        cep: profile.billingData.cep || '',
        logradouro: profile.billingData.logradouro || '',
        numero: profile.billingData.numero || '',
        complemento: profile.billingData.complemento || '',
        bairro: profile.billingData.bairro || '',
        cidade: profile.billingData.cidade || '',
        estado: profile.billingData.estado || '',
      });
    }
  }, [profile, resetBilling]);

  const handleSaveBilling = async (data) => {
    try {
      setIsSavingBilling(true);
      await updateProfile({ billingData: data });
      resetBilling(data);
    } finally {
      setIsSavingBilling(false);
    }
  };

  // Busca endereço pelo CEP via ViaCEP
  const handleCEPBlur = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setBillingValue('logradouro', data.logradouro || '', { shouldDirty: true });
        setBillingValue('bairro', data.bairro || '', { shouldDirty: true });
        setBillingValue('cidade', data.localidade || '', { shouldDirty: true });
        setBillingValue('estado', data.uf || '', { shouldDirty: true });
      }
    } catch {
      // silencioso — usuario pode preencher manualmente
    }
  };

  const themeOptions = [
    { value: 'light', label: 'Claro', icon: Sun },
    { value: 'dark', label: 'Escuro', icon: Moon },
    { value: 'system', label: 'Sistema', icon: Monitor },
  ];

  const planLabels = { free: 'Gratuito', pro: 'Pro', family: 'Família' };

return (
    <div className="space-y-6">
      {/* Avatar e info básica */}
      <Card title="Informações Pessoais">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6">
          <div className="relative">
            <Avatar name={profile?.name} src={profile?.avatar} size="xl" />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <button
              className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
              title="Alterar foto"
              onClick={handleAvatarClick}
              disabled={isUploadingAvatar}
            >
              {isUploadingAvatar ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            </button>
          </div>
          <div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {profile?.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {profile?.email}
            </p>
            <span className="inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
              Plano {planLabels[tenant?.plan] || 'Gratuito'}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit(handleSave)} className="space-y-4 max-w-md">
          <Input
            label="Nome completo"
            leftIcon={<User size={16} />}
            error={errors.name?.message}
            {...register('name')}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              E-mail
            </label>
            <input
              type="email"
              value={profile?.email || ''}
              disabled
              className="block w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed text-sm"
            />
            <p className="mt-1 text-xs text-gray-400">
              O e-mail não pode ser alterado
            </p>
          </div>

          <div className="flex justify-end">
            <Button type="submit" isLoading={isUpdating} disabled={!isDirty}>
              Salvar Alterações
            </Button>
          </div>
        </form>
      </Card>

      {/* Dados de Faturamento */}
      <Card
        title="Dados de Faturamento"
        subtitle={
          <span className="flex items-center gap-1.5 text-xs">
            {isPaidPlan ? (
              billingComplete ? (
                <><CheckCircle2 size={13} className="text-emerald-500" /> Dados completos — utilizados na emissão de nota fiscal</>
              ) : (
                <><AlertCircle size={13} className="text-amber-500" /> Necessário para emissão de nota fiscal do seu plano</>
              )
            ) : (
              <><FileText size={13} className="text-gray-400" /> Preenchimento opcional agora — obrigatório ao assinar um plano pago</>
            )}
          </span>
        }
      >
        {/* Banner informativo para plano free */}
        {!isPaidPlan && (
          <div className="flex gap-3 p-4 mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <FileText size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                Por que preencher agora?
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                Ao assinar um plano pago, precisaremos desses dados para emitir a nota fiscal.
                Preenchendo agora, a ativação do plano será instantânea.
              </p>
            </div>
          </div>
        )}

        {/* Banner de alerta para plano pago sem dados */}
        {isPaidPlan && !billingComplete && (
          <div className="flex gap-3 p-4 mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                Dados incompletos
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                Preencha os dados abaixo para que possamos emitir sua nota fiscal corretamente.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmitBilling(handleSaveBilling)} className="space-y-5">
          {/* CPF */}
          <div className="max-w-xs">
            <Input
              label={<span>CPF <span className="text-gray-400 font-normal text-xs">(pessoa física)</span></span>}
              placeholder="000.000.000-00"
              leftIcon={<FileText size={16} />}
              error={billingErrors.cpf?.message}
              {...registerBilling('cpf', {
                onChange: (e) => {
                  e.target.value = formatCPF(e.target.value);
                },
              })}
            />
          </div>

          {/* CEP com auto-preenchimento */}
          <div className="max-w-xs">
            <Input
              label="CEP"
              placeholder="00000-000"
              leftIcon={<MapPin size={16} />}
              error={billingErrors.cep?.message}
              {...registerBilling('cep', {
                onChange: (e) => {
                  e.target.value = formatCEP(e.target.value);
                },
                onBlur: handleCEPBlur,
              })}
            />
            <p className="mt-1 text-xs text-gray-400">Digite o CEP para preencher o endereço automaticamente</p>
          </div>

          {/* Logradouro + Número */}
          <div className="grid grid-cols-3 gap-4 max-w-lg">
            <div className="col-span-2">
              <Input
                label="Logradouro"
                placeholder="Rua, Av., Alameda..."
                error={billingErrors.logradouro?.message}
                {...registerBilling('logradouro')}
              />
            </div>
            <div>
              <Input
                label="Número"
                placeholder="123"
                error={billingErrors.numero?.message}
                {...registerBilling('numero')}
              />
            </div>
          </div>

          {/* Complemento */}
          <div className="max-w-xs">
            <Input
              label={<span>Complemento <span className="text-gray-400 font-normal text-xs">(opcional)</span></span>}
              placeholder="Apto 42, Sala 5..."
              error={billingErrors.complemento?.message}
              {...registerBilling('complemento')}
            />
          </div>

          {/* Bairro + Cidade + Estado */}
          <div className="grid grid-cols-3 gap-4 max-w-lg">
            <div>
              <Input
                label="Bairro"
                placeholder="Centro"
                error={billingErrors.bairro?.message}
                {...registerBilling('bairro')}
              />
            </div>
            <div>
              <Input
                label="Cidade"
                placeholder="São Paulo"
                error={billingErrors.cidade?.message}
                {...registerBilling('cidade')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Estado
              </label>
              <select
                className={clsx(
                  'block w-full px-3 py-2 rounded-lg border text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
                  billingErrors.estado
                    ? 'border-red-500 bg-white dark:bg-gray-800'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
                )}
                {...registerBilling('estado')}
              >
                <option value="">UF</option>
                {ESTADOS.map((uf) => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </select>
              {billingErrors.estado && (
                <p className="mt-1 text-xs text-red-500">{billingErrors.estado.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button
              type="submit"
              isLoading={isSavingBilling}
              disabled={!isBillingDirty}
            >
              Salvar Dados de Faturamento
            </Button>
          </div>
        </form>
      </Card>

      {/* Tema */}
      <Card title="Aparência">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Escolha o tema da interface
        </p>
        <div className="flex gap-3">
          {themeOptions.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => value !== 'system' && setTheme(value)}
              disabled={value === 'system'}
              className={clsx(
                'flex flex-col items-center gap-2 px-5 py-4 rounded-xl border-2 transition-all text-sm font-medium',
                theme === value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300',
                value === 'system' && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Icon size={22} />
              {label}
            </button>
          ))}
        </div>
      </Card>

      {/* Detalhes da conta */}
      <Card title="Detalhes da Conta">
        <dl className="space-y-3">
          {[
            { label: 'ID da conta', value: profile?.userId },
            { label: 'Membro desde', value: profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('pt-BR') : '—' },
            { label: 'Último acesso', value: profile?.lastLoginAt ? new Date(profile.lastLoginAt).toLocaleDateString('pt-BR') : '—' },
            { label: 'Função', value: profile?.role === 'owner' ? 'Proprietário' : 'Membro' },
          ].map(({ label, value }) => (
            <div key={label} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
              <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
              <dd className="text-sm font-medium text-gray-900 dark:text-white font-mono">{value || '—'}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </div>
  );
};

export default Profile;
